<template>
    <div class="flex flex-col w-80 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-3 gap-2 border border-gray-200 dark:border-gray-600">
        <!-- Search Bar -->
        <div class="flex flex-row items-center justify-center w-full bg-gray-100 dark:bg-gray-700 rounded-md p-2">
            <div class="relative w-full">
                <div class="absolute inset-y-0 start-0 flex items-center pointer-events-none ps-2">
                    <MagnifyingGlassIcon class="size-5 text-gray-400" />
                </div>
                <input 
                    type="text" 
                    v-model="search" 
                    placeholder="Search anything..." 
                    class="w-full reset-input border-none focus:ring-0 focus:outline-none bg-transparent ps-8 text-sm text-slate-700 dark:text-slate-300" 
                />
            </div>
        </div>

        <!-- Menu Items -->
        <div class="flex flex-1 flex-col w-full gap-0.5 max-h-96 overflow-y-auto">
            <template v-for="(menus, groupName) in groupedMenus" :key="groupName">
                <!-- Group Header -->
                <div 
                    v-if="Object.keys(groupedMenus).length > 1 || (Object.keys(groupedMenus).length === 1 && groupName !== 'ungrouped')"
                    class="px-3 py-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider sticky top-0 bg-white dark:bg-gray-800 z-10"
                >
                    {{ groupName === 'ungrouped' ? 'Other' : groupName }}
                </div>
                
                <!-- Menu Items in Group -->
                <div 
                    v-for="menu in menus" 
                    :key="menu.id || menu.label"
                    @click="handleMenuClick(menu)"
                    @mouseenter="hoveredMenuId = menu.id || menu.label"
                    @mouseleave="hoveredMenuId = null"
                    class="flex flex-col items-center justify-between  cursor-pointer transition-all group"
                    
                >
                    <div class="flex flex-row items-center justify-between w-full px-3 py-2 rounded-md"
                    :class="{
                        'text-white bg-gradient-to-r from-indigo-500 to-purple-500 dark:bg-gradient-to-r dark:from-indigo-900 dark:to-purple-900': hoveredMenuId === (menu.id || menu.label),
                        'hover:bg-gray-50 dark:hover:bg-gray-700': hoveredMenuId !== (menu.id || menu.label)
                    }">
                        <div class="flex flex-row items-center gap-3 flex-1 min-w-0">
                             <!-- Favorite Button -->
                            <button
                                v-if="menu.allowFavorite !== false"
                                @click.stop="toggleFavorite(menu)"
                                @touchstart.prevent="toggleFavorite(menu)"
                                class="flex-shrink-0 p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors hover:bg-black/10 dark:hover:bg-white/10 group-hover:text-white dark:group-hover:text-white"
                                :title="isFavorite(menu) ? 'Remove from favorites' : 'Add to favorites'"
                            >
                                <StarIcon 
                                    class="size-4 transition-colors"
                                    :class="isFavorite(menu) 
                                        ? 'text-yellow-500 fill-yellow-500 group-hover:text-yellow-300 group-hover:fill-yellow-300 dark:group-hover:text-yellow-300 dark:group-hover:fill-yellow-300' 
                                        : 'text-gray-400 dark:text-gray-500 group-hover:text-white dark:group-hover:text-white'"
                                />
                            </button>
                            <div v-else class="w-6"></div>
                            <!-- Icon -->
                            <div class="flex-shrink-0">
                                <component 
                                    :is="menu.icon" 
                                    v-if="menu.icon"
                                    v-bind="menu.iconProps"
                                    class="size-5 text-gray-700 dark:text-gray-300 group-hover:text-white group-active:text-white dark:group-hover:text-white dark:group-active:text-white"
                                />
                            </div>
                            
                            <!-- Label -->
                            <span class="text-sm text-gray-700 dark:text-gray-300 flex-1 truncate group-hover:text-white group-active:text-white dark:group-hover:text-white dark:group-active:text-white">
                                {{ menu.label }}
                            </span>
                            
                            <!-- Keyboard Shortcut -->
                            <span 
                                v-if="menu.shortcut" 
                                class="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0 group-hover:text-white group-active:text-white dark:group-hover:text-white dark:group-active:text-white"
                            >
                                {{ formatShortcut(menu.shortcut) }}
                            </span>
                        </div>

                        <!-- Component Arrow Indicator -->
                        <button
                            v-if="menu.component"
                            @click.stop="toggleComponent(menu)"
                            class="flex-shrink-0 ml-2 p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors hover:bg-black/10 dark:hover:bg-white/10 group-hover:text-white dark:group-hover:text-white"
                            :title="isComponentExpanded(menu) ? 'Collapse component' : 'Expand component'"
                        >
                            <ChevronDownIcon 
                                v-if="isComponentExpanded(menu)"
                                class="size-4 transition-transform text-gray-400 dark:text-gray-500 group-hover:text-white dark:group-hover:text-white"
                            />
                            <ChevronRightIcon 
                                v-else
                                class="size-4 transition-transform text-gray-400 dark:text-gray-500 group-hover:text-white dark:group-hover:text-white"
                            />
                        </button>

                        <!-- Toggle Switch -->
                        <div v-if="menu.toggle !== undefined || menu.toggleValue !== undefined" class="flex-shrink-0 ml-2" 
                            @click="toggleMenu(menu)">
                            <div 
                                class="relative w-11 h-6 rounded-full transition-colors duration-200"
                                :class="getToggleValue(menu) ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'"
                            >
                                <div 
                                    class="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-200"
                                    :class="getToggleValue(menu) ? 'translate-x-5' : 'translate-x-0'"
                                ></div>
                            </div>
                        </div>

                       
                    </div>
                    <Transition name="fade" mode="out-in">
                        <div v-if="menu.component && isComponentVisible(menu)" class="w-full border-b border-gray-200 dark:border-gray-600">
                            <component :is="menu.component" 
                            v-bind="menu.props || {}" 
                            v-on="menu.events || {}" />
                        </div>
                    </Transition>
                </div>
            </template>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits, onMounted } from 'vue'
import { MagnifyingGlassIcon, StarIcon, ChevronRightIcon, ChevronDownIcon } from '@heroicons/vue/24/outline'
import { fetchFavoriteMenuIds, saveFavoriteMenuIds } from '@/components/common/menus/menus.js'

const props = defineProps({
    menus: {
        type: Array,
        required: true,
        validator: (menus) => {
            return menus.every(menu => 
                menu.label && 
                (menu.action || menu.callback || menu.toggle !== undefined || menu.toggleValue !== undefined)
            )
        }
    },
    footer: {
        type: Object,
        default: null,
        validator: (footer) => {
            if (!footer) return true
            return footer.date && footer.lastEditedBy
        }
    }
})

const emit = defineEmits(['menu-click', 'toggle-change', 'favorite-change'])

const search = ref('')
const hoveredMenuId = ref(null)
const favoriteMenuIds = ref([])
const expandedMenuIds = ref(new Set())

const observerMenuChange = (menuId, changes) => {
    const menu = props.menus.find(m => m.id === menuId);
    if (menu) {
        Object.entries(changes).forEach(([key, value]) => {
            if (menu[key] && menu[key].value !== undefined) {
                menu[key].value = value;
            } else {
                menu[key] = value;
            }
        });
    }
}
// Get visible value from menu (handles both refs and booleans)
const getVisibleValue = (menu) => {
    if (menu.visible === undefined) {
        return undefined
    }
    if (menu.visible.value !== undefined) {
        return menu.visible.value
    }
    return menu.visible
}


onMounted(() => {
    favoriteMenuIds.value = fetchFavoriteMenuIds();
    
    // Initialize expanded menus based on visible property
    props.menus.forEach(menu => {
        if (menu.component) {
            const id = menu.id || menu.label
            // If menu has visible property set to true, expand it initially
            const visibleValue = getVisibleValue(menu)
            if (visibleValue === true) {
                expandedMenuIds.value.add(id)
            }
        }
        if (menu.onExternalChange) {
            menu.onExternalChange(observerMenuChange);
        }

        if (menu.load) {
            menu.load();
        }
    })

    emit('loaded', { favoriteMenuIds: Array.from(favoriteMenuIds.value) })
})

// Filter menus based on search
const filteredMenus = computed(() => {
    let result = props.menus.filter(menu => {
        const isHidden = menu.hidden !== undefined && menu.hidden.value !== undefined ? menu.hidden.value : menu.hidden;
        return !isHidden;
    })
    
    if (search.value.trim()) {
        const searchLower = search.value.toLowerCase()
        result = result.filter(menu => 
            menu.label.toLowerCase().includes(searchLower) ||
            (menu.shortcut && menu.shortcut.toLowerCase().includes(searchLower))
        )
    }
    
    // Sort: favorites first, then alphabetically
    return result.sort((a, b) => {
        const aIsFavorite = isFavorite(a)
        const bIsFavorite = isFavorite(b)
        
        if (aIsFavorite && !bIsFavorite) return -1
        if (!aIsFavorite && bIsFavorite) return 1
        return a.label.localeCompare(b.label)
    })
})

// Group menus by their group property
const groupedMenus = computed(() => {
    const groups = {}
    
    filteredMenus.value.forEach(menu => {
        const groupName = menu.group || 'ungrouped'
        if (!groups[groupName]) {
            groups[groupName] = []
        }
        groups[groupName].push(menu)
    })
    
    // Sort groups: favorites first within each group, then alphabetically
    Object.keys(groups).forEach(groupName => {
        groups[groupName].sort((a, b) => {
            const aIsFavorite = isFavorite(a)
            const bIsFavorite = isFavorite(b)
            
            if (aIsFavorite && !bIsFavorite) return -1
            if (!aIsFavorite && bIsFavorite) return 1
            return a.label.localeCompare(b.label)
        })
    })
    
    // Sort group keys alphabetically, but put 'ungrouped' at the end
    const sortedGroupKeys = Object.keys(groups).sort((a, b) => {
        if (a === 'ungrouped') return 1
        if (b === 'ungrouped') return -1
        return a.localeCompare(b)
    })
    
    const sortedGroups = {}
    sortedGroupKeys.forEach(key => {
        sortedGroups[key] = groups[key]
    })
    
    return sortedGroups
})

// Format keyboard shortcut for display
const formatShortcut = (shortcut) => {
    if (!shortcut) return ''
    
    // Replace common modifiers
    return shortcut
        .replace(/cmd/gi, '⌘')
        .replace(/ctrl/gi, '⌃')
        .replace(/alt/gi, '⌥')
        .replace(/shift/gi, '⇧')
        .replace(/up/gi, '↑')
        .replace(/down/gi, '↓')
        .replace(/left/gi, '←')
        .replace(/right/gi, '→')
        .replace(/\s+/g, ' ')
        .trim()
}

// Handle menu item click
const handleMenuClick = (menu) => {
    if (menu.toggle !== undefined || menu.toggleValue !== undefined) {
        // Don't trigger action on toggle items, only toggle
        return
    }
    
    if (menu.action) {
        menu.action(menu)
    } else if (menu.callback) {
        menu.callback(menu)
    }
    
    emit('menu-click', menu)
}

// Get toggle value (handles both refs and booleans)
const getToggleValue = (menu) => {
    if (menu.toggleValue !== undefined) {
        return menu.toggleValue.value !== undefined ? menu.toggleValue.value : menu.toggleValue
    }
    if (menu.toggle !== undefined) {
        return menu.toggle.value !== undefined ? menu.toggle.value : menu.toggle
    }
    return false
}


// Toggle menu item (for toggle switches)
const toggleMenu = (menu) => {
    
    if (menu.toggleValue !== undefined) {
        if (menu.toggleValue.value !== undefined) {
            menu.toggleValue.value = !menu.toggleValue.value
        } else {
            menu.toggleValue = !menu.toggleValue
        }
        
        if (menu.onToggle) {
            menu.onToggle(getToggleValue(menu), menu)
        }
        
        emit('toggle-change', menu)
    } else if (menu.toggle !== undefined) {
        if (menu.toggle.value !== undefined) {
            menu.toggle.value = !menu.toggle.value
        } else {
            menu.toggle = !menu.toggle
        }
        
        if (menu.onToggle) {
            menu.onToggle(getToggleValue(menu), menu)
        }
        
        emit('toggle-change', menu)
    }
}

// Check if menu is favorite
const isFavorite = (menu) => {
    const id = menu.id || menu.label
    return favoriteMenuIds.value.includes(id)
}

// Toggle favorite status
const toggleFavorite = (menu) => {
    if (favoriteMenuIds.value.includes(menu.id)) {
        favoriteMenuIds.value = favoriteMenuIds.value.filter(id => id !== menu.id)
    } else {
        favoriteMenuIds.value.push(menu.id)
    }
    
    // Save to localStorage
    saveFavoriteMenuIds(favoriteMenuIds.value)
    
    emit('favorite-change', { menu, isFavorite: favoriteMenuIds.value.includes(menu.id) })
}

// Check if component is expanded
const isComponentExpanded = (menu) => {
    const id = menu.id || menu.label
    return expandedMenuIds.value.has(id)
}

// Check if component should be visible (expanded or visible property is true)
const isComponentVisible = (menu) => {
    const visibleValue = getVisibleValue(menu)
    
    // If menu has visible property explicitly set, use it
    if (visibleValue !== undefined) {
        return visibleValue === true
    }
    
    // Otherwise, check if it's expanded
    return isComponentExpanded(menu)
}

// Toggle component expansion
const toggleComponent = (menu) => {
    const id = menu.id || menu.label
    const wasExpanded = expandedMenuIds.value.has(id)
    
    if (wasExpanded) {
        expandedMenuIds.value.delete(id)
    } else {
        expandedMenuIds.value.add(id)
    }
    
    // Update menu.visible if it's a ref (only if visible property exists)
    // This allows programmatic control through visible property
    if (menu.visible !== undefined && menu.visible.value !== undefined) {
        menu.visible.value = !wasExpanded
    }
}
</script>